import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import log from 'loglevel'
import { PlaybookInputs } from '@trustero/trustero-api-web/lib/playbook/playbook_pb'
import { Chat, ChatMessage } from '@trustero/trustero-api-web/lib/chat/chat_pb'
import { ClientMessageType } from '@trustero/trustero-api-web/lib/tichatapi/agent_pb'
import {
  useCreateAppCopilotChat,
  useUpdateChat,
} from 'src/pages/TrusteroIntelligence/trusteroIntelligence.hooks'
import { ReferringContext, ReferringContextMetadata } from './AppCopilot.types'

const MIN_WIDTH_PERCENT = 33
const MAX_WIDTH_PERCENT = 66
const DEFAULT_WIDTH_PERCENT = 33
const DEFAULT_WIDTH_PX = 700

const getLocalStorageKey = (objectType: number, objectId?: string): string =>
  `copilot_last_chat_${objectType}_${objectId || ''}`

const writeLastVisitedChat = (
  objectType: number,
  objectId: string | undefined,
  chatId: string,
): void => {
  try {
    localStorage.setItem(getLocalStorageKey(objectType, objectId), chatId)
  } catch {
    // ignore storage errors
  }
}

const readLastVisitedChat = (objectType: number, objectId?: string): string => {
  try {
    return localStorage.getItem(getLocalStorageKey(objectType, objectId)) ?? ''
  } catch {
    return ''
  }
}

type AppCopilotContextValue = {
  title: string
  setTitle: (title: string) => void
  show: boolean
  setShow: (show: boolean) => void
  showBotTrigger: boolean
  view: 'library' | 'conversation'
  setView: (view: 'library' | 'conversation') => void
  historyOpen: boolean
  setHistoryOpen: (open: boolean) => void
  chatId?: string
  setChatId: (chatId?: string) => void
  referringContext: ReferringContext
  referringContextMetadata?: ReferringContextMetadata
  setReferringContextMetadata: (
    referringContextMetadata?: ReferringContextMetadata,
  ) => void
  currentContextLabel?: string
  setCurrentContextLabel: (currentContextLabel?: string) => void
  isCurrentContextDismissed: boolean
  setIsCurrentContextDismissed: (dismissed: boolean) => void
  widthPercent: number
  setWidthPercent: (widthPercent: number) => void
  sendMessage: (
    text: string,
    playbookInputs?: PlaybookInputs,
    fileUploads?: File[],
  ) => Promise<void>
  registerSendMessage: (
    sendMessage: (
      text: string,
      playbookInputs?: PlaybookInputs,
      fileUploads?: File[],
    ) => Promise<void>,
  ) => () => void
  createCopilotChat: (
    chatTitle: string,
    leadPrompt?: string,
    skipViewSwitch?: boolean,
  ) => Promise<{ chat: Chat }>
  writeLastVisitedChat: (chatId: string) => void
  pendingPlaybookLaunch?: PlaybookInputs
  setPendingPlaybookLaunch: (inputs?: PlaybookInputs) => void
  notifyConfirmationSent: (
    clientMessageType: ClientMessageType,
    artifactId: string,
    artifactType: number,
  ) => void
  notifyLatestAgentMessage: (message: ChatMessage) => void
}

const AppCopilotContext = createContext<AppCopilotContextValue | undefined>(
  undefined,
)

type PendingSendMessage = {
  text: string
  playbookInputs?: PlaybookInputs
  fileUploads?: File[]
}

const clampWidthPercent = (widthPercent: number): number =>
  Math.max(MIN_WIDTH_PERCENT, Math.min(MAX_WIDTH_PERCENT, widthPercent))

const getDefaultWidthPercent = (): number => {
  if (typeof window === 'undefined' || window.innerWidth <= 0) {
    return DEFAULT_WIDTH_PERCENT
  }
  return (DEFAULT_WIDTH_PX / window.innerWidth) * 100
}

export const AppCopilotProvider = ({
  children,
  referringContext,
  referringContextMetadata,
  currentContextLabel,
  initialTitle,
  initialShow = false,
  initialShowBotTrigger = true,
  initialWidthPercent = DEFAULT_WIDTH_PERCENT,
  onConfirmationSent,
  onLatestAgentMessage,
}: React.PropsWithChildren<{
  referringContext: ReferringContext
  referringContextMetadata?: ReferringContextMetadata
  currentContextLabel?: string
  initialTitle: string
  initialShow?: boolean
  initialShowBotTrigger?: boolean
  initialWidthPercent?: number
  onConfirmationSent?: (
    clientMessageType: ClientMessageType,
    artifactId: string,
    artifactType: number,
  ) => void
  onLatestAgentMessage?: (message: ChatMessage) => void
}>): JSX.Element => {
  const [title, setTitle] = useState(initialTitle)
  const [show, setShow] = useState(initialShow)
  const [showBotTrigger] = useState(initialShowBotTrigger)
  const [view, setView] = useState<'library' | 'conversation'>('library')
  const [historyOpen, setHistoryOpen] = useState(false)
  const [chatId, setChatId] = useState<string>()
  const [currentContextLabelState, setCurrentContextLabel] = useState<
    string | undefined
  >(currentContextLabel)
  const [referringContextMetadataState, setReferringContextMetadata] = useState<
    ReferringContextMetadata | undefined
  >(referringContextMetadata)
  const [widthPercent, setWidthPercentState] = useState(
    clampWidthPercent(
      initialWidthPercent === DEFAULT_WIDTH_PERCENT
        ? getDefaultWidthPercent()
        : initialWidthPercent,
    ),
  )
  const [isCurrentContextDismissed, setIsCurrentContextDismissed] =
    useState(false)
  const [pendingPlaybookLaunch, setPendingPlaybookLaunch] =
    useState<PlaybookInputs>()
  const onConfirmationSentRef = useRef(onConfirmationSent)
  useEffect(() => {
    onConfirmationSentRef.current = onConfirmationSent
  }, [onConfirmationSent])

  const onLatestAgentMessageRef = useRef(onLatestAgentMessage)
  useEffect(() => {
    onLatestAgentMessageRef.current = onLatestAgentMessage
  }, [onLatestAgentMessage])

  const notifyConfirmationSent = useCallback(
    (
      clientMessageType: ClientMessageType,
      artifactId: string,
      artifactType: number,
    ) => {
      onConfirmationSentRef.current?.(
        clientMessageType,
        artifactId,
        artifactType,
      )
    },
    [],
  )

  const notifyLatestAgentMessage = useCallback((message: ChatMessage) => {
    onLatestAgentMessageRef.current?.(message)
  }, [])

  const sendMessageRef = useRef<
    | ((
        text: string,
        playbookInputs?: PlaybookInputs,
        fileUploads?: File[],
      ) => Promise<void>)
    | undefined
  >()
  const pendingMessagesRef = useRef<PendingSendMessage[]>([])
  const isFlushingPendingRef = useRef(false)
  const createAppCopilotChatFn = useCreateAppCopilotChat()
  const updateChatFn = useUpdateChat()

  const setWidthPercent = (next: number) => {
    setWidthPercentState(clampWidthPercent(next))
  }

  const setCurrentContextLabelSafe = useCallback(
    (next?: string) => {
      setCurrentContextLabel((prev) => (prev === next ? prev : next))
    },
    [setCurrentContextLabel],
  )

  const setReferringContextMetadataSafe = useCallback(
    (next?: ReferringContextMetadata) => {
      setReferringContextMetadata((prev) => {
        const prevFilterQuery = prev?.filterQuery || ''
        const nextFilterQuery = next?.filterQuery || ''
        const prevResultsCount = prev?.resultsCount || 0
        const nextResultsCount = next?.resultsCount || 0
        const prevResultIds = prev?.resultIds || []
        const nextResultIds = next?.resultIds || []
        if (
          prevFilterQuery === nextFilterQuery &&
          prevResultsCount === nextResultsCount &&
          prevResultIds.length === nextResultIds.length &&
          prevResultIds.every((id, index) => id === nextResultIds[index])
        ) {
          return prev
        }
        return next
      })
    },
    [setReferringContextMetadata],
  )

  useEffect(() => {
    setCurrentContextLabelSafe(currentContextLabel)
  }, [currentContextLabel, setCurrentContextLabelSafe])

  useEffect(() => {
    setReferringContextMetadataSafe(referringContextMetadata)
  }, [referringContextMetadata, setReferringContextMetadataSafe])

  // Reset context-dismissed state when referring context changes
  useEffect(() => {
    setIsCurrentContextDismissed(false)
  }, [referringContext.objectType, referringContext.objectId])

  const registerSendMessage = useCallback(
    (
      sendMessage: (
        text: string,
        playbookInputs?: PlaybookInputs,
        fileUploads?: File[],
      ) => Promise<void>,
    ) => {
      sendMessageRef.current = sendMessage
      if (
        !isFlushingPendingRef.current &&
        pendingMessagesRef.current.length > 0
      ) {
        void (async () => {
          isFlushingPendingRef.current = true
          try {
            while (pendingMessagesRef.current.length > 0) {
              const next = pendingMessagesRef.current.shift()
              if (!next) {
                break
              }
              try {
                await sendMessage(
                  next.text,
                  next.playbookInputs,
                  next.fileUploads,
                )
              } catch (error) {
                // Put it back at the front so it can be retried later.
                pendingMessagesRef.current.unshift(next)
                log.error('AppCopilot queued sendMessage failed', error)
                break
              }
            }
          } finally {
            isFlushingPendingRef.current = false
          }
        })()
      }
      return () => {
        if (sendMessageRef.current === sendMessage) {
          sendMessageRef.current = undefined
        }
      }
    },
    [],
  )

  const sendMessage = useCallback(
    async (
      text: string,
      playbookInputs?: PlaybookInputs,
      fileUploads?: File[],
    ) => {
      if (!sendMessageRef.current) {
        pendingMessagesRef.current.push({ text, playbookInputs, fileUploads })
        setShow(true)
        log.error('AppCopilot sendMessage queued until chat panel is ready')
        return
      }
      try {
        await sendMessageRef.current(text, playbookInputs, fileUploads)
      } catch (error) {
        log.error('AppCopilot sendMessage failed', error)
      }
    },
    [setShow],
  )

  const writeLastVisitedChatCb = useCallback(
    (chatIdToWrite: string) => {
      writeLastVisitedChat(
        referringContext.objectType,
        referringContext.objectId,
        chatIdToWrite,
      )
    },
    [referringContext.objectType, referringContext.objectId],
  )

  const createCopilotChat = useCallback(
    async (
      chatTitle: string,
      leadPrompt?: string,
      skipViewSwitch?: boolean,
    ) => {
      const { chat } = await createAppCopilotChatFn(
        referringContext,
        chatTitle,
        referringContextMetadataState,
      )
      if (leadPrompt) {
        chat.setLeadPrompt(leadPrompt)
        await updateChatFn(chat)
      }
      if (!skipViewSwitch) {
        const newChatId = chat.getId()
        setChatId(newChatId)
        writeLastVisitedChat(
          referringContext.objectType,
          referringContext.objectId,
          newChatId,
        )
        setView('conversation')
        setIsCurrentContextDismissed(false)
      }
      return { chat }
    },
    [
      createAppCopilotChatFn,
      updateChatFn,
      referringContext,
      referringContextMetadataState,
    ],
  )

  const contextValue = useMemo<AppCopilotContextValue>(
    () => ({
      title,
      setTitle,
      show,
      setShow,
      showBotTrigger,
      view,
      setView,
      historyOpen,
      setHistoryOpen,
      chatId,
      setChatId,
      referringContext,
      referringContextMetadata: referringContextMetadataState,
      setReferringContextMetadata: setReferringContextMetadataSafe,
      currentContextLabel: currentContextLabelState,
      setCurrentContextLabel: setCurrentContextLabelSafe,
      isCurrentContextDismissed,
      setIsCurrentContextDismissed,
      widthPercent,
      setWidthPercent,
      sendMessage,
      registerSendMessage,
      createCopilotChat,
      writeLastVisitedChat: writeLastVisitedChatCb,
      pendingPlaybookLaunch,
      setPendingPlaybookLaunch,
      notifyConfirmationSent,
      notifyLatestAgentMessage,
    }),
    [
      title,
      show,
      showBotTrigger,
      view,
      historyOpen,
      chatId,
      referringContext,
      referringContextMetadataState,
      currentContextLabelState,
      isCurrentContextDismissed,
      setReferringContextMetadataSafe,
      setCurrentContextLabelSafe,
      widthPercent,
      sendMessage,
      registerSendMessage,
      createCopilotChat,
      writeLastVisitedChatCb,
      pendingPlaybookLaunch,
      notifyConfirmationSent,
      notifyLatestAgentMessage,
    ],
  )

  return (
    <AppCopilotContext.Provider value={contextValue}>
      {children}
    </AppCopilotContext.Provider>
  )
}

export const useAppCopilotContext = (): AppCopilotContextValue => {
  const context = useContext(AppCopilotContext)
  if (!context) {
    throw new Error(
      'useAppCopilotContext must be used within an AppCopilotProvider',
    )
  }
  return context
}

export const useOptionalAppCopilotContext = ():
  | AppCopilotContextValue
  | undefined => useContext(AppCopilotContext)

export { readLastVisitedChat }
